import { useRef, type ReactNode } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight, FlaskConical } from "lucide-react";
import { useHive } from "@/store/useHive";
import { useSampleDataCta } from "@/hooks/useSampleDataCta";
import { APPS } from "@/data/apps";
import { isEnabled } from "@/lib/entitlements";
import { WS_STATUS_LABEL } from "@/data/workspaces";
import type { ProductId } from "@/api/types";
import { WorkspacesWidget } from "@/components/desktop/WorkspacesWidget";
import { TasksWidget } from "@/components/desktop/TasksWidget";
import { IntegrationsWidget } from "@/components/desktop/IntegrationsWidget";
import { WorkflowWidget } from "@/components/desktop/WorkflowWidget";
import { ExecutionCenterWidget } from "@/components/desktop/ExecutionCenterWidget";
import { AppLaunchpad } from "@/components/desktop/AppLaunchpad";
import type { AppId } from "@/types";

function Widget({
  children,
  initial,
  className = "",
  delay = 0,
}: {
  children: ReactNode;
  /** anchor offsets in px; side decides which edge to pin to */
  initial: { side: "left" | "right" | "center"; x: number; y: number };
  className?: string;
  delay?: number;
}) {
  const anchor =
    initial.side === "left"
      ? { left: initial.x }
      : initial.side === "right"
        ? { right: initial.x }
        : { left: window.innerWidth / 2 + initial.x };
  // Widgets are draggable, but their content is clickable — swallow the click
  // the browser fires right after a drag ends so repositioning never opens apps.
  const dragging = useRef(false);
  return (
    <motion.div
      drag
      dragMomentum={false}
      dragElastic={0.08}
      onDragStart={() => {
        dragging.current = true;
      }}
      onDragEnd={() => {
        setTimeout(() => {
          dragging.current = false;
        }, 0);
      }}
      onClickCapture={(e) => {
        if (dragging.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      whileDrag={{ scale: 1.03, zIndex: 30, cursor: "grabbing" }}
      style={{ ...anchor, top: initial.y }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 24, delay }}
      className={`glass absolute cursor-grab rounded-2xl border border-app p-5 shadow-window ${className}`}
    >
      {children}
    </motion.div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

const QUICK_LABELS: Record<ProductId, string> = {
  ares: "Verify",
  garlic: "Specialize",
  athena: "Readiness",
  byonc: "Routing",
  appgraph: "Graph",
};

export function Desktop() {
  const openApp = useHive((s) => s.openApp);
  const tasks = useHive((s) => s.tasks);
  const ready = tasks.filter((t) => t.status === "ready").length;
  const workspaces = useHive((s) => s.codeWorkspaces);
  const activeWorkspaceId = useHive((s) => s.activeWorkspaceId);
  const activeWs = workspaces.find((w) => w.id === activeWorkspaceId);
  const entitlements = useHive((s) => s.entitlements);
  const sampleCta = useSampleDataCta();
  const aresEnabled = isEnabled(entitlements, "ares");
  const freshAccount = tasks.length === 0 && workspaces.length === 0;

  // Quick-launch chips only surface products this tenant can actually use.
  const quick: { id: AppId; label: string }[] = [
    ...(Object.keys(QUICK_LABELS) as ProductId[])
      .filter((p) => isEnabled(entitlements, p))
      .map((p) => ({ id: p as AppId, label: QUICK_LABELS[p] })),
    { id: "launcher", label: "All apps" },
  ];

  return (
    <div className="mesh-bg absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-[0.55]" />

      {/* Ambient welcome — centered, non-interactive backdrop text */}
      <div className="pointer-events-none absolute left-1/2 top-[7%] -translate-x-1/2 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="text-[11px] font-medium uppercase tracking-[0.4em] text-app-2">
            Momentum AI · Workspace
          </div>
          <h1 className="mt-2 bg-gradient-to-br from-app to-app-1 bg-clip-text text-5xl font-semibold tracking-tight text-app">
            Welcome to Hive
          </h1>
          <p className="mt-2 text-sm text-app-2">
            The operating system for AI-powered software delivery
          </p>
        </motion.div>
      </div>

      {/* AI assistant — top-left spotlight. Entitlement-aware: never mentions a
          product this tenant hasn't enabled; fresh accounts get onboarding. */}
      <Widget initial={{ side: "left", x: 40, y: 88 }} className="w-[300px]" delay={0.3}>
        <div className="flex items-center gap-2 text-xs font-medium text-app-2">
          <Sparkles size={14} className="accent" /> AI Assistant
        </div>
        {freshAccount ? (
          <>
            <p className="mt-3 break-words text-sm leading-relaxed text-app [overflow-wrap:anywhere]">
              {greeting()}. Welcome to Hive — connect an integration, create a workspace, or
              explore a fully-populated demo to see how everything fits together.
            </p>
            {sampleCta ? (
              <button
                onClick={sampleCta.run}
                className="mt-4 flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-xs font-semibold text-black transition hover:brightness-105"
              >
                <FlaskConical size={13} /> {sampleCta.label}
              </button>
            ) : (
              <button
                onClick={() => openApp("workspaces")}
                className="mt-4 flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-xs font-semibold text-black transition hover:brightness-105"
              >
                Create a workspace <ArrowUpRight size={13} />
              </button>
            )}
          </>
        ) : (
          <>
            <p className="mt-3 break-words text-sm leading-relaxed text-app [overflow-wrap:anywhere]">
              {greeting()}. <span className="accent font-medium">{ready} {ready === 1 ? "task is" : "tasks are"}</span>{" "}
              {aresEnabled ? "refined and ready for ARES" : "refined and ready to assign"}
              {activeWs ? (
                <>
                  , and <span className="font-medium">{activeWs.repo}</span> is{" "}
                  <span className="font-medium">{WS_STATUS_LABEL[activeWs.status].toLowerCase()}</span>.
                </>
              ) : (
                <>. Create a workspace to start building with agents.</>
              )}
            </p>
            <button
              onClick={() => openApp("tasks")}
              className="mt-4 flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-xs font-semibold text-black transition hover:brightness-105"
            >
              Review task queue <ArrowUpRight size={13} />
            </button>
          </>
        )}
      </Widget>

      {/* Workspaces — left */}
      <Widget initial={{ side: "left", x: 40, y: 300 }} delay={0.42}>
        <WorkspacesWidget />
      </Widget>

      {/* Tasks — top-right */}
      <Widget initial={{ side: "right", x: 40, y: 88 }} delay={0.5}>
        <TasksWidget />
      </Widget>

      {/* Integrations — right */}
      <Widget initial={{ side: "right", x: 40, y: 360 }} delay={0.58}>
        <IntegrationsWidget />
      </Widget>

      {/* App launchpad — center flagship, one card per Momentum product */}
      <Widget initial={{ side: "center", x: -305, y: 196 }} delay={0.56}>
        <AppLaunchpad />
      </Widget>

      {/* Workflow Builder — bottom-left, under Workspaces */}
      <Widget initial={{ side: "left", x: 40, y: 584 }} delay={0.64}>
        <WorkflowWidget />
      </Widget>

      {/* Execution Center — bottom-right, the heartbeat */}
      <Widget initial={{ side: "right", x: 40, y: 578 }} delay={0.7}>
        <ExecutionCenterWidget />
      </Widget>

      {/* Quick launch surface — bottom center, above dock. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-32 flex justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="pointer-events-auto flex items-center gap-2"
        >
          {quick.map((q) => {
            const app = APPS[q.id];
            return (
              <button
                key={q.id}
                onClick={() => openApp(q.id)}
                className="glass group flex items-center gap-2 rounded-full border border-app px-3.5 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong"
              >
                <app.icon size={14} style={{ color: app.accent }} />
                {q.label}
              </button>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}
